import {ViewOptions} from 'backbone';
import {ScaleLinear} from 'd3';

import {CircularLayout, RingLayout} from 'molbio/maps/circular/CircularLayout';
import CircularMapView from 'molbio/maps/circular/CircularMap';
import {GenericArcDatum} from 'molbio/maps/circular/CircularMapTypedefs';
import {CircularMapState} from 'molbio/maps/MapStates';
import SelectableView from 'molbio/maps/SelectableView';
import CircularMapTabState from 'molbio/tabstates/CircularMapTabState';
import {getAngles, toStandardAngle} from 'util/geometry/GeometryUtil';

export interface RingComponentOptions<TLayout extends RingLayout> extends ViewOptions<CircularMapState> {
  /* A linear function mapping an index in base pairs to an angle in radians */
  angle: ScaleLinear<number, number>;
  /*
   * An object containing all the layouts of all the rings in the circular map. Currently, this is only used
   * by LabelsView, which needs to calculate the radius at which to draw the labels for multiple types of
   * sequence features.
   */
  circularLayout?: CircularLayout;
  /* The layout object for this ring */
  layout: TLayout;
  /*
   * A reference back to the Circular Map Backbone view. Currently only used by the legacy Backbone ORFs view
   * and the LabelsView, which needs access to the set of sequence features being rendered by multiple labeled
   * ring views.
   */
  circularView: CircularMapView;
  /* The Backbone tabstate model for the Circular Map tab */
  tabstate: CircularMapTabState;
}

export default abstract class RingComponent<
  TLayout extends RingLayout,
> extends SelectableView<CircularMapState> {
  isLinear = false;
  tabstate!: CircularMapTabState;
  angle!: ScaleLinear<number, number>;
  layout!: TLayout;
  circularView!: CircularMapView;

  initialize(options: RingComponentOptions<TLayout>): void {
    this.tabstate = options.tabstate;
    this.angle = options.angle;
    this.layout = options.layout;
    this.circularView = options.circularView;
  }

  registerListeners(): void {
    this.listenTo(this.layout, 'change:visible', () => {
      if (this.redrawPlasmid()) {
        this.triggerRedraw();
      } else {
        this.update();
      }
    });
    const option = this.tabstate.get('visibilityOptions').getOption(this.className);
    if (option != null) {
      return this.listenTo(option.get('settings'), 'change:value', () => {
        if (this.redrawPlasmid()) {
          this.triggerRedraw();
        } else {
          this.update();
        }
      });
    }
  }

  basePairToAngle(basepair: number): number {
    return toStandardAngle(this.angle(basepair));
  }

  getAngles(start: number, end: number | null): GenericArcDatum {
    return getAngles(start, end, this.model.get('sequence').length(), this.angle);
  }

  die(): void {
    this.stopListening();
  }

  redraw(): void {}

  update(): void {
    if (this.model.get('sequence').length() < 1) {
      this.$el.hide();
      return;
    }
    this.$el.show();
    this.redraw();
  }

  redrawPlasmid(): boolean {
    return false;
  }

  triggerRedraw(): void {
    this.model.trigger('redraw');
  }
}
